/**
 * Code component prerender HTML can include React streaming boundary comments
 * (`<!--$-->` / `<!--/$-->`). Atoms SSR strips those from the full page stream;
 * we swap them for Webflow-specific comments before embedding so they survive
 * that pass, then restore React's markers in render-atoms for correct hydration.
 */
const CODE_COMPONENT_STREAM_OPEN_MARKER = '<!--wf-cc-stream-open-->';
const CODE_COMPONENT_STREAM_CLOSE_MARKER = '<!--wf-cc-stream-close-->';

const REACT_STREAM_OPEN_MARKER = '<!--$-->';
const REACT_STREAM_CLOSE_MARKER = '<!--/$-->';

/**
 * Replaces React's streaming markers with Webflow's Code Component streaming markers.
 *
 *   - <!--$-->  → <!--wf-cc-stream-open-->
 *   - <!--/$--> → <!--wf-cc-stream-close-->
 */
export function encodeCodeComponentStreamingMarkers(html: string): string {
  return html
    .replaceAll(REACT_STREAM_OPEN_MARKER, CODE_COMPONENT_STREAM_OPEN_MARKER)
    .replaceAll(REACT_STREAM_CLOSE_MARKER, CODE_COMPONENT_STREAM_CLOSE_MARKER);
}

/**
 * Replaces Webflow's Code Component streaming markers with React's streaming markers.
 *
 *   - <!--wf-cc-stream-open-->  → <!--$-->
 *   - <!--wf-cc-stream-close--> → <!--/$-->
 */
export function decodeCodeComponentStreamingMarkers(html: string): string {
  return html
    .replaceAll(CODE_COMPONENT_STREAM_OPEN_MARKER, REACT_STREAM_OPEN_MARKER)
    .replaceAll(CODE_COMPONENT_STREAM_CLOSE_MARKER, REACT_STREAM_CLOSE_MARKER);
}

/**
 * Removes internal streaming markers from React's server-rendered HTML.
 *
 * Specifically targets:
 *   - <!--$-->  → ''
 *   - <!--/$--> → ''
 */
export function removeReactStreamingMarkers(html: string): string {
  return html.replace(/<!--\/?\$-->/g, '');
}

/**
 * Cleans up React's streaming markers from the HTML.
 *
 * Specifically targets:
 *   - <!--$-->     → start of a streaming marker
 *   - <!--/$-->    → end of a streaming marker
 *
 * Context:
 * - These markers are used by React during streaming SSR to mark content that may load asynchronously.
 * - We're using `onAllReady`, so all content has been fully flushed server-side.
 * - No pending (`<!--$?-->`) or fallback (`<!--$!-->`) markers should be present.
 * - Our client-side rendering does not rely on these markers, so they can be safely removed.
 * - Code component prerender output uses Webflow markers comments so those
 *   segments are not stripped here; `decodeCodeComponentStreamingMarkers`
 *   restores React markers for the final HTML.
 * - Regular HTML comments (e.g., <!-- developer note -->) are preserved.
 */
export function cleanupReactStreamingMarkers(html: string): string {
  // IMPORTANT: order is important here:
  // 1. First we remove the streaming placeholders from the HTML.
  html = removeReactStreamingMarkers(html);
  // 2. Then we restore the streaming placeholders in Code Components HTML only
  html = decodeCodeComponentStreamingMarkers(html);
  return html;
}
